import { useCallback, useEffect, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import camelcaseKeys from 'camelcase-keys';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router';

import { Logger } from '@core';
import { ApiConfig } from '@data';
import { userAtom } from '@store';

import { supabaseBoothClient, supabaseClient } from '../client';

type SessionCreateResponse = WithError<{
    data: {
        accessToken: string;
        refreshToken: string;
    };
}>;

const getBoothQueryKey = () => ['booth'];

export const useBooth = () => {
    return useQuery({
        queryKey: getBoothQueryKey(),
        queryFn: async () => {
            const {
                data: { user },
                error,
            } = await supabaseBoothClient.auth.getUser();
            Logger.info(`BOOTH LOAD: boothId=${user?.id}`);
            if (error?.code) {
                Logger.error(`${error.name}: ${error.code}`);
                throw error;
            }
            return user;
        },
        // `useBoothEvents` update user state, because of that, we should disable all polling and just listen to the events.
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        staleTime: Infinity,
    });
};

export const useBoothEvents = () => {
    const queryClient = useQueryClient();
    useEffect(() => {
        const {
            data: { subscription },
        } = supabaseBoothClient.auth.onAuthStateChange((event, session) => {
            Logger.info(`BOOTH EVENT: ${event}, ${session?.user?.id}`);

            if (event === 'SIGNED_OUT') {
                queryClient.setQueryData(getBoothQueryKey(), null);
            }
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                queryClient.setQueryData(getBoothQueryKey(), session?.user ?? null);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [queryClient]);
};

type BoothQRCodeResponse = WithError<{
    data: {
        expiresAt: string;
        qrCode: string;
    };
}>;

const getBoothQRCodeQueryKey = () => ['booth', 'qr'];
export const useBoothQRCode = () => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: getBoothQRCodeQueryKey(),
        queryFn: async () => {
            const { data: response, error } =
                await supabaseBoothClient.functions.invoke<BoothQRCodeResponse>('booth-qr-create');

            Logger.info(`BOOTH QRCODE: at: ${!!response?.data.qrCode}`);

            if (error || !response?.data) {
                const errorBody = await error?.context?.json?.();
                Logger.error(`BOOTH QRCODE ERROR: at: ${errorBody?.error ?? error.message}`);
                throw new Error(errorBody?.error ?? error.message ?? 'No QR code generated');
            }

            return response.data;
        },
    });

    useEffect(() => {
        if (!query.data?.expiresAt) return;

        const msUntilExpiry = new Date(query.data.expiresAt).getTime() - Date.now();
        const timeout = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: getBoothQRCodeQueryKey() });
        }, msUntilExpiry);

        return () => clearTimeout(timeout);
    }, [query.data?.expiresAt, queryClient]);

    return query;
};

export const useBoothLogin = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async (body: Record<'email' | 'password', string>) => {
            const { data: response, error } = await supabaseBoothClient.functions.invoke<SessionCreateResponse>(
                'booth-login',
                {
                    method: 'POST',
                    body,
                },
            );

            Logger.info(`BOOTH LOGIN: at: ${!!response?.data?.accessToken} rt:  ${!!response?.data?.refreshToken}`);

            if (error || !response?.data.accessToken) {
                const errorBody = await error?.context?.json?.();
                Logger.error(
                    `BOOTH LOGIN ERROR: ${errorBody || 'No error body'}, ${error.message || 'No error message'}`,
                );
                throw new Error(errorBody?.error ?? error.message ?? 'Unable to create session');
            }

            const { error: sessionError, data: sessionData } = await supabaseBoothClient.auth.setSession({
                access_token: response.data.accessToken,
                refresh_token: response.data.refreshToken,
            });
            if (sessionError) {
                Logger.error(
                    `BOOTH LOGIN ERROR, SET SESSION: ${sessionError.name} ${sessionError.message} ${sessionError.code}`,
                );
                throw sessionError;
            }
            return sessionData;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

export const useBoothLogout = () => {
    const { data: boothSession } = useBoothSession();
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async () => {
            Logger.info(`BOOTH LOGOUT`);

            if (!boothSession) {
                throw new Error('No booth session id');
            }
            const { error } = await supabaseBoothClient
                .from('booth_sessions')
                .update({
                    ended_at: new Date().toISOString(),
                })
                .eq('id', boothSession.id);
            if (error) {
                Logger.error(`BOOTH LOGOUT ERROR: ${error.message}`);
                throw error;
            }
            const { error: logoutError } = await supabaseBoothClient.auth.signOut();
            if (error) {
                Logger.error(`BOOTH LOGOUT ERROR: ${logoutError?.name} ${logoutError?.message} ${logoutError?.code}`);
                throw logoutError;
            }
            return;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

/**
 * Used only by events that come from the booth sessions table.
 */
export const useBoothUserAutoLogin = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async () => {
            const { data: response, error } =
                await supabaseBoothClient.functions.invoke<SessionCreateResponse>('booth-user-session-create');

            Logger.info(
                `BOOTH USER AUTO LOGIN: at: ${!!response?.data?.accessToken} rt:  ${!!response?.data?.refreshToken}`,
            );

            if (error || !response?.data) {
                const errorBody = await error?.context?.json?.();
                Logger.error(`BOOTH USER LOGIN ERROR: ${errorBody?.error ?? error.message}`);
                throw new Error(errorBody?.error ?? error.message ?? 'Unable to log in into booth');
            }

            const { error: sessionError, data: sessionData } = await supabaseClient.auth.setSession({
                access_token: response.data.accessToken,
                refresh_token: response.data.refreshToken,
            });
            if (sessionError) {
                Logger.error(
                    `BOOTH USER AUTO LOGIN ERROR, SET SESSION: ${sessionError.name} ${sessionError.message} ${sessionError.code}`,
                );
                throw sessionError;
            }
            return sessionData;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
        retry: 3,
        retryDelay: 1000,
    });
};

/**
 * Used only by events that come from the booth sessions table.
 */
export const useBoothUserAutoLogout = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const setUserAtom = useSetAtom(userAtom);
    return useMutation({
        mutationFn: async () => {
            Logger.info(`BOOTH USER AUTO LOGOUT`);
            const { error } = await supabaseClient.auth.signOut({
                scope: 'local',
            });
            if (error) {
                Logger.info(`BOOTH USER AUTO LOGOUT ERROR ${error}`);
                throw error;
            }
        },
        onSuccess: async () => {
            queryClient.removeQueries({ queryKey: ['user'] });
            setUserAtom(null);
            navigate('/', { replace: true });
        },
    });
};

export const useBoothUserLogin = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async (body: Record<'email' | 'password', string>) => {
            const { data: response, error } = await supabaseBoothClient.functions.invoke<BoothSessionCreateResponse>(
                'booth-user-login',
                {
                    method: 'POST',
                    body,
                },
            );

            Logger.info(`BOOTH USER LOGIN: ${response?.data?.userId}`);

            if (error) {
                const errorBody = await error.context?.json?.();
                Logger.error(`BOOTH USER LOGIN ERROR: ${errorBody?.error ?? error.message}`);
                throw new Error(errorBody?.error ?? error.message);
            }
            return response?.data;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

export const useBoothUserConfirmEmail = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async ({ email, token }: Record<'email' | 'token', string>) => {
            const { error } = await supabaseBoothClient.functions.invoke<WithError>('booth-user-register', {
                method: 'POST',
                body: {
                    email,
                    token,
                },
            });

            Logger.info(`BOOTH USER CONFIRM EMAIL`);
            if (error) {
                const errorBody = await error.context?.json?.();
                Logger.error(`BOOTH USER CONFIRM EMAIL ERROR: ${errorBody?.error ?? error.message}`);
                throw new Error(errorBody?.error ?? error.message);
            }
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

export const useBoothUserLogout = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async () => {
            const { data: response, error } =
                await supabaseBoothClient.functions.invoke<BoothSessionCreateResponse>('booth-user-logout');
            Logger.info(`BOOTH USER LOGOUT: ${response?.data.userId}`);

            if (error) {
                const errorBody = await error.context?.json?.();
                Logger.error(`BOOTH USER LOGOUT ERROR: ${errorBody?.error ?? error.message}`);
                throw new Error(errorBody?.error ?? error.message);
            }
            return response?.data;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

const getBoothInfoQueryKey = (id: string) => ['booth', id, 'info'];

export const useBoothInfo = () => {
    const { data: booth } = useBooth();
    return useQuery({
        queryKey: getBoothInfoQueryKey(booth?.id ?? ''),
        queryFn: async () => {
            const { data, error } = await supabaseBoothClient
                .from('booths')
                .select('*')
                .eq('booth_user_id', booth!.id)
                .limit(1)
                .single();
            Logger.info(`BOOTH INFO: ${data?.id}`);

            if (error) {
                Logger.error(`BOOTH INFO ERROR: ${error?.message}`);
                throw error;
            }
            return camelcaseKeys(data!, { deep: true });
        },
        enabled: !!booth?.id,
        refetchOnMount: false,
    });
};

type BoothSessionCreateResponse = WithError<{
    data: {
        boothId: number | null;
        calibrationId: number | null;
        createdAt: string;
        endedAt: string | null;
        expiresAt: string;
        forceRefreshAt: string | null;
        id: number;
        sessionUid: string;
        userId: string | null;
    };
}>;

type WithError<T = Record<any, any>> = {
    error?: string;
} & T;

const getBoothSessionQueryKey = (id: string) => ['booth', id, 'boothSession'];
const BOOTH_SESSION_FORCE_REFRESH_KEY = 'booth-session-force-refresh';

type BoothSessionForceRefreshState = {
    lastRefresh: string | null;
    boothSessionId: number;
};

type BoothSessionRefreshOptions = {
    listenForForceRefresh?: boolean;
};

export const useBoothSession = () => {
    const { data: booth } = useBooth();
    return useQuery({
        queryKey: getBoothSessionQueryKey(booth?.id ?? ''),
        queryFn: async () => {
            const { error, data: response } =
                await supabaseBoothClient.functions.invoke<BoothSessionCreateResponse>('booth-session-create');

            Logger.info(`BOOTH SESSION: boothId: ${response?.data.boothId}, userId: ${response?.data.userId}`);

            if (error || !response?.data) {
                const errorBody = await error?.context?.json?.();
                Logger.error(`BOOTH SESSION ERROR: ${errorBody?.error ?? error.message}`);

                throw new Error(errorBody?.error ?? error.message);
            }

            return response.data;
        },
        enabled: !!booth?.id,
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        staleTime: 30 * 1_000,
    });
};

export const useBoothSessionForceRefresh = () => {
    const { data: boothSession } = useBoothSession();

    return useMutation({
        mutationFn: async () => {
            if (!boothSession) {
                Logger.error('BOOTH SESSION FORCE REFRESH ERROR: No booth session id');
                return;
            }

            const { error } = await supabaseBoothClient
                .from('booth_sessions')
                .update({
                    force_refresh_at: new Date().toISOString(),
                })
                .eq('id', boothSession.id);

            if (error) {
                Logger.error(`BOOTH SESSION FORCE REFRESH ERROR: ${error.message}`);
            }
        },
    });
};

/**
 * @usage `kiosk | floor`
 *
 * Consume booth session. If session exists and no user is logged in,
 * but `user_id` is provided in booth session. Auto login into that user.
 */
export const useBoothSessionManager = () => {
    const { mutateAsync: loginBoothUser } = useBoothUserAutoLogin();
    const { mutateAsync: logoutBoothUser } = useBoothUserAutoLogout();
    const { data: boothSession } = useBoothSession();
    const userId = boothSession?.userId;

    const userIdRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (userId === userIdRef.current) {
            return;
        }
        userIdRef.current = userId;

        if (!userId) {
            logoutBoothUser();
        } else {
            loginBoothUser();
        }
    }, [loginBoothUser, logoutBoothUser, userId]);
};

/**
 * @usage `kiosk | floor`
 *
 * Sets beast endpoint in application scope.
 */
export const useBoothEndpoint = () => {
    const { data: boothInfo } = useBoothInfo();

    useEffect(() => {
        ApiConfig.baseUrl = boothInfo?.beastEndpoint;
    }, [boothInfo?.beastEndpoint]);
};
/**
 * @usage `kiosk | floor`
 *
 * Refreshes/invalidates `useBoothSession` query whenever `booth_sessions` db table updates
 * AND whenever booth auth state changes.
 */
export const useBoothSessionRefresh = ({ listenForForceRefresh = false }: BoothSessionRefreshOptions = {}) => {
    const queryClient = useQueryClient();

    const { data: booth } = useBooth();
    const { data: boothInfo } = useBoothInfo();
    const { data: boothSession } = useBoothSession();

    ApiConfig.baseUrl = boothInfo?.beastEndpoint;

    const refreshBoothSession = useCallback(() => {
        Logger.info('BOOTH SESSION REFRESH');
        queryClient.invalidateQueries({
            queryKey: getBoothSessionQueryKey(booth?.id ?? ''),
        });
    }, [booth?.id, queryClient]);

    const forceRefreshFloor = useCallback(
        (forceRefreshAt?: string | null) => {
            if (!boothSession?.id) return;

            if (typeof forceRefreshAt !== 'string') return;

            const refreshState: BoothSessionForceRefreshState = JSON.parse(
                localStorage.getItem(BOOTH_SESSION_FORCE_REFRESH_KEY) ?? '{}',
            );

            if (refreshState.boothSessionId !== boothSession.id || refreshState.lastRefresh === forceRefreshAt) {
                return;
            }

            window.location.reload();
        },
        [boothSession?.id],
    );

    useEffect(() => {
        if (!booth?.id || !boothInfo?.id) return;

        if (listenForForceRefresh) {
            if (!boothSession?.id) return;

            localStorage.setItem(
                BOOTH_SESSION_FORCE_REFRESH_KEY,
                JSON.stringify({
                    lastRefresh: boothSession.forceRefreshAt,
                    boothSessionId: boothSession.id,
                }),
            );
        }

        const subscription = supabaseBoothClient
            .channel('auth_booth_sessions')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'booth_sessions',
                    filter: `booth_id=eq.${boothInfo?.id}`,
                },
                () => refreshBoothSession(),
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'booth_sessions',
                    filter: `booth_id=eq.${boothInfo?.id}`,
                },
                (payload) => {
                    refreshBoothSession();

                    if (!listenForForceRefresh) return;
                    forceRefreshFloor(payload?.new?.['force_refresh_at']);
                },
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [
        booth?.id,
        boothInfo?.id,
        boothSession?.forceRefreshAt,
        boothSession?.id,
        forceRefreshFloor,
        listenForForceRefresh,
        refreshBoothSession,
    ]);
};
